import Panzoom from '@panzoom/panzoom';

class PageZoom {
  constructor() {
    this.modal = null;
    this.image = null;
    this.panzoomInstance = null;
    this._pagesList = null;
    this._currentIndex = -1;
    this.prevBtn = null;
    this.nextBtn = null;
    this.active = false;
    this._userHasInteracted = false; // becomes true after first pan/gesture
    this._initialAlignmentDone = false; // track first alignment
  }

  init() {
    this.modal = document.getElementById('page-zoom-modal');
    this.image = this.modal && this.modal.querySelector('.zoom-image');
    if (!this.modal || !this.image) return;
    this.image.style.transformOrigin = 'top left';

    // Navigation buttons
    this.prevBtn = this.modal.querySelector('.zoom-previous');
    this.nextBtn = this.modal.querySelector('.zoom-next');
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => { e.stopPropagation(); this.goRelative(-1); });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => { e.stopPropagation(); this.goRelative(1); });
    }

    // Panzoom with contain outside; custom alignment afterwards
    this.panzoomInstance = Panzoom(this.image, {
      contain: 'outside',
      maxScale: 3,
      minScale: 1,
      cursor: 'grab'
    });

    // Initial alignment
    this.recalcAlignment();

    // Mark interaction on user input (pointer/touch/mouse)
    const markInteraction = () => { this._userHasInteracted = true; };
    ['pointerdown','touchstart','mousedown','wheel'].forEach(evt => {
      this.image.addEventListener(evt, markInteraction, { passive: true });
    });

    // Clamp on change
    this.image.addEventListener('panzoomchange', (e) => {
      const { scale, x, y } = e.detail;
      const clamped = this._clampPan(scale, x, y);
      if (clamped.x !== x || clamped.y !== y) {
        this.panzoomInstance.pan(clamped.x, clamped.y);
      }
    });

    // Accessibility
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.setAttribute('aria-modal', 'true');

    const closeBtn = this.modal.querySelector('.zoom-close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.close(); });

    // Bind page images
    document.querySelectorAll('.page-image').forEach(img => {
      if (!img.dataset.zoomBound) {
        img.dataset.zoomBound = '1';
        img.addEventListener('click', (e) => { e.preventDefault(); this.open(img.src); });
      }
    });
  }

  open(src) {
    // If not already set, build the pages list and current index
    if (!this._pagesList) {
      this._pagesList = Array.from(document.querySelectorAll('.page-image')).map(img => img.closest('.page'));
    }
    if (this._currentIndex === -1 && this._pagesList) {
      // Find the index of the page containing the image with this src
      const idx = this._pagesList.findIndex(page => {
        const img = page && page.querySelector('.page-image');
        return img && img.src === src;
      });
      if (idx !== -1) this._currentIndex = idx;
    }
    if (!this.modal || !this.image) return;

    this.active = true;
    this.modal.style.display = 'flex';
    this.modal.removeAttribute('aria-hidden');
    document.documentElement.classList.add('zoom-open');
    // Reset flags for fresh image so initial alignment runs
    this._userHasInteracted = false;
    this._initialAlignmentDone = false;
    this.image.onload = () => {
      // Reset pan/zoom & enforce top-left anchor
      if (this.panzoomInstance) {
        this.panzoomInstance.zoom(1);
        this.panzoomInstance.pan(0, 0);
      }
      this.recalcAlignment();
      this._updateAspectClass();
    };
    this.image.src = src;
    // Update scroll progress bar to match modal page
    if (window.nav && typeof window.nav.setProgressBar === 'function' && typeof window.nav.getScrollPercent === 'function') {
      window.nav.setProgressBar(window.nav.getScrollPercent());
    }
  }

  close() {
    if (!this.modal || !this.image) return;
    this.modal.style.display = 'none';
    this.modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('zoom-open');
    this.active = false;
    this._currentIndex = -1;

    if (this.panzoomInstance) {
      // Just reset instead of dispose so we keep listener bindings
      this.panzoomInstance.zoom(1);
      this.panzoomInstance.pan(0, 0);
    }
    // Update scroll progress bar to match scroll position after modal closes
    if (window.nav && typeof window.nav.setProgressBar === 'function' && typeof window.nav.getScrollPercent === 'function') {
      window.nav.setProgressBar(window.nav.getScrollPercent());
    }
  }

  goRelative(delta) {
      if (!this.active || !this._pagesList || this._currentIndex === -1) return;
      const newIndex = this._currentIndex + delta;
      if (newIndex < 0 || newIndex >= this._pagesList.length) return;
      this.loadPageAt(newIndex);
      // Sync pages-container scroll, but do NOT close zoom modal
      const pageEl = this._pagesList[newIndex];
      const pageId = pageEl && (pageEl.getAttribute('data-id') || pageEl.id);
      if (pageId && window.nav && typeof window.nav.scrollToPage === 'function') {
        window.nav.scrollToPage({ pageId, skipZoomClose: true });
      }
  }

  loadPageAt(index) {
  // Defensive: update _currentIndex
  this._currentIndex = index;
    if (!this._pagesList || index < 0 || index >= this._pagesList.length) return;
    const pageEl = this._pagesList[index];
    const img = pageEl.querySelector('.page-image');
    if (!img) return;
    this._currentIndex = index;
    this._originPage = pageEl;
    this._originImg = img;
    // Reset pan for new image
    this.panX = 0;
    this.image.style.transition = 'none';
    // Force zoomed-out baseline before swapping source (so no flash of previous zoom state)
    if (this.panzoomInstance) {
      this.panzoomInstance.zoom(1);
      this.panzoomInstance.pan(0,0);
    } else {
      this.image.style.transform = 'translate(0px,0px) scale(1)';
    }
    // Swap source; when loaded, ensure still baseline (in case Panzoom adjusted internally)
    const newSrc = img.src;
    // Reset interaction/alignment flags for new page
    this._userHasInteracted = false;
    this._initialAlignmentDone = false;
    this.image.onload = () => {
      if (this.panzoomInstance) {
        this.panzoomInstance.zoom(1);
        this.panzoomInstance.pan(0,0);
      }
  this.recalcAlignment();
      this._updateAspectClass();
    };
    this.image.src = newSrc; // rely on caching; if not loaded yet onload will recalc paddings
    this.updateNavButtons();
  }

  _updateAspectClass() {
    if (!this.modal || !this.image) return;
    const isPortrait = this.image.naturalHeight > this.image.naturalWidth;
    this.modal.classList.toggle('zoom-portrait', isPortrait);
    this.modal.classList.toggle('zoom-landscape', !isPortrait);
  }

  updateNavButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    const disablePrev = (!this._pagesList || this._currentIndex <= 0);
    const disableNext = (!this._pagesList || this._currentIndex === -1 || this._currentIndex >= this._pagesList.length - 1);
    this.setNavDisabled(this.prevBtn, disablePrev);
    this.setNavDisabled(this.nextBtn, disableNext);
  }

  setNavDisabled(btn, disabled) {
    if (!btn) return;
    if (disabled) {
      btn.classList.add('is-disabled');
      btn.setAttribute('aria-hidden', 'true');
      btn.setAttribute('tabindex', '-1');
    } else {
      btn.classList.remove('is-disabled');
      btn.setAttribute('aria-hidden', 'false');
      btn.setAttribute('tabindex', '0');
    }
  }

  _clampPan(scale, x, y) {
    // Compute allowed pan so image stays inside container
    const parent = this.image.parentElement;
    if (!parent) return { x, y };
    const parentW = parent.offsetWidth;
    const parentH = parent.offsetHeight;
    const naturalW = this.image.naturalWidth || this.image.offsetWidth;
    const naturalH = this.image.naturalHeight || this.image.offsetHeight;
    const scaledW = naturalW * scale;
    const scaledH = naturalH * scale;

    // Center offsets Panzoom would apply (distance from left/top when centered)
    const centerOffsetX = (parentW - scaledW) / 2; // positive if image narrower
    const centerOffsetY = (parentH - scaledH) / 2; // positive if image shorter

    // When image is smaller than container, allow anchoring at -centerOffset (top-left)
    // Otherwise clamp within symmetric range like before.
    const userFree = this._userHasInteracted; // after first interaction allow free clamp instead of forced anchor

    // Horizontal
    if (scaledW < parentW) {
      // Image narrower: initial anchor left; after interaction allow slight pan but clamp within left anchor
      const leftAnchor = -centerOffsetX; // negative value pulling image to left/top origin
      if (!userFree) {
        x = leftAnchor; // force anchor before interaction
      } else {
        // Allow minimal overscroll? clamp to same anchor (no horizontal movement possible)
        x = leftAnchor;
      }
    } else {
      const maxOffsetX = (scaledW - parentW) / 2;
      // Symmetric clamp; no forced anchor once user interacts
      if (!userFree) {
        // prefer left/top reveal: bias toward -maxOffsetX without snapping back after interaction
        x = Math.max(-maxOffsetX, Math.min(maxOffsetX, x));
      } else {
        x = Math.max(-maxOffsetX, Math.min(maxOffsetX, x));
      }
    }

    // Vertical
    if (scaledH < parentH) {
      const topAnchorShort = -centerOffsetY; // shift upward to remove vertical centering
      if (!userFree) {
        y = topAnchorShort;
      } else {
        y = topAnchorShort; // no vertical pan possible when shorter
      }
    } else {
      const maxOffsetY = (scaledH - parentH) / 2;
      if (!userFree) {
        // Initial top anchor: show top edge (positive translation moves image down)
        y = maxOffsetY;
      } else {
        // Allow free vertical pan within bounds
        y = Math.max(-maxOffsetY, Math.min(maxOffsetY, y));
      }
    }
    return { x, y };
  }

  // Recalculate alignment so portrait / narrow images with contain centering get anchored top-left
  recalcAlignment() {
    if (!this.image) return;
    // Once user has interacted, avoid forcing alignment again (prevents snap-back on late triggers)
    if (this._userHasInteracted && this._initialAlignmentDone) return;
    const parent = this.image.parentElement;
    if (!parent) return;
    const parentW = parent.offsetWidth;
    const parentH = parent.offsetHeight;
    const naturalW = this.image.naturalWidth || this.image.offsetWidth;
    const naturalH = this.image.naturalHeight || this.image.offsetHeight;
    const scale = 1; // baseline zoom
    const scaledW = naturalW * scale;
    const scaledH = naturalH * scale;
    let offsetX = 0;
    let offsetY = 0;
    if (scaledW < parentW) offsetX = -(parentW - scaledW) / 2; // left anchor
    if (scaledH < parentH) {
      offsetY = -(parentH - scaledH) / 2; // top anchor for short image
    } else {
      offsetY = (scaledH - parentH) / 2; // top edge visible for tall image
    }
    if (this.panzoomInstance) this.panzoomInstance.pan(offsetX, offsetY);
    this.image.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(1)`;
    this._initialAlignmentDone = true;
  }
}

let pageZoom = new PageZoom();
export default pageZoom;